import { FC, ReactNode, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useMediaQueryMatch } from "@/hooks/useMediaQueryMatch";
import { Redirect, Route, RouteComponentProps, Router, Switch, useLocation } from "wouter";
import { AuthenticatedRoute } from "./components/Common/AuthenticatedRoute/AuthenticatedRoute";
import { ErrorViewer } from "./components/Common/ErrorViewer/ErrorViewer";
import { getDefaultRouteAfterLogin } from "./constants";
import { useExplorerOnlyMode } from "./hooks/useExplorerOnlyMode";
import { useNotificationStore } from "./store/notification";
import { ucfirst } from "./utils/string";
import { useQueryParams } from "./hooks/useQueryParams";
import { useAuthState } from "./store/auth";
import { AuthProcess } from "./components/Auth/AuthProcess";
import env from "./env";
import { Toaster } from "./components/ui/toaster";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useToolLocation } from "./hooks/useToolLocation";
import { useMainStore } from "./store/main";
import { installBetterstack } from "./utils/betterstack";
import { LazyComponent } from "./components/Common/LazyComponent/LazyComponent";
import { MainHeader } from "./components/Common/MainHeader/MainHeader";
import { ExplorerHeader } from "./components/Common/ExplorerHeader/ExplorerHeader";
import { MainFooter } from "./components/Common/MainFooter/MainFooter";
import { PageWrapper } from "./components/Common/PageWrapper/PageWrapper";
import { LeftSidebar } from "./components/Common/LeftSidebar/LeftSidebar";
import { CookieWidget } from "./components/Common/CookieWidget/CookieWidget";
import { CrossIcon } from "@/components/Icons/CrossIcon";
import { HelpViewer } from "./components/Common/HelpViewer/HelpViewer";
import { explorerPrefixedPath, urlWithCurrentQuery } from "./utils";
import { redirectUsingQuery } from "./utils/location";
import { getToken } from "./utils/api";
import MaintenancePage from "./pages/Common/MaintenancePage";
import CreateVirtualMachinePageV2 from "./pages/Cloud/CreateVirtualMachinePageV2";
import CreateContainerPageV2 from "./pages/Cloud/CreateContainerPageV2";
import CanonicalLink from "./components/CanonicalLink";
import { DocumentTitle } from "./components/Common/DocumentTitle/DocumentTitle";
import { DocumentTitleOverrideProvider } from "./contexts/DocumentTitleOverrideContext";
import { ConditionalAuthRoute } from "./components/Common/ConditionalAuthRoute/ConditionalAuthRoute";
import { RequiresLoginModal } from "./components/RequiresLoginModal/RequiresLoginModal";
import { RequestClusterModal } from "./components/Common/RequestClusterModal/RequestClusterModal";
import { useModalStore } from "./store/modal";
import { useUserStore } from "./store/user";
import { SupportWidget } from "./components/Common/SupportWidget/SupportWidget";
import { AnalyticsEventType } from "./constants/analytics";
import { getFirstTouchAttributions } from "./utils/analytics";
import { trackEvent } from "./utils/analytics";
import ENV_VARIABLES from "./env";
import { LoginVerificationLoader } from "./components/Auth/LoginVerificationLoader";
import localCookies from "./utils/cookie";
import { getSubdomainToolHomePath } from "./utils/tool";
import { prefetchCompetitorPriceMap } from "./components/ClustersPage/VerticalFlowComponents/priceComparison";
import { getVariant } from "./utils/amplitude";
import { FEATURE_FLAGS, FeatureFlags } from "./constants/featureFlags";
import { RedirectToClusterDetailsPage } from "./pages/Cloud/RedirectToClusterDetailsPage";
import { useClearClusterDraftOnNavigateAway } from "./hooks/useClearClusterDraftOnNavigateAway";
import { useDocumentTitle } from "./hooks/useDocumentTitle";

const IntelligenceDefaultPage = () => {
  const isLoggedIn = useAuthState((state) => state.status === "authenticated");

  return <Redirect to={isLoggedIn ? "/ai/app/new" : "/ai/models"} />;
};

/** Clears create-cluster drafts on SPA leave; must sit inside Router. */
const ClusterDraftNavigationGuard = () => {
  useClearClusterDraftOnNavigateAway();
  return null;
};

export const App: FC = () => {
  const displayError = useNotificationStore((state) => state.displayError);
  const queryParams = useQueryParams<{
    key: string;
    errorTitle?: string;
    errorMessage?: string;
  }>();
  const hook = useToolLocation();
  const updateToolToLocation = useMainStore((state) => state.updateToolToLocation);
  const tool = useMainStore((state) => state.tool);
  const isAppContentFullWidth = useMainStore((state) => state.isAppContentFullWidth);
  const sidebarCollapsed = useMainStore((state) => state.sidebarCollapsed);
  const mobileSidebarSheetOpen = useMainStore((state) => state.mobileSidebarSheetOpen);
  const setMobileSidebarSheetOpen = useMainStore((state) => state.setMobileSidebarSheetOpen);
  const isMdUp = useMediaQueryMatch("md");
  const [location] = useLocation();
  const [isWarningBannerVisible, setIsWarningBannerVisible] = useState(true);
  const [isExplorerWarningBannerVisible, setIsExplorerWarningBannerVisible] = useState(true);
  const isRequiresLoginModalOpen = useUserStore((state) => state.isRequiresLoginModalOpen);
  const setRequiresLoginModalOpen = useUserStore((state) => state.setRequiresLoginModalOpen);
  const requiresLoginMode = useUserStore((state) => state.requiresLoginMode);
  const isAuthReady = useAuthState((state) => state.isReady);
  const isLoggedIn = useAuthState((state) => state.status === "authenticated");
  const theme = useUserStore((state) => state.theme);
  const appTheme = useUserStore((state) => state.appTheme);
  const updateTheme = useUserStore((state) => state.updateTheme);
  const applyThemeForCurrentMode = useUserStore((state) => state.applyThemeForCurrentMode);

  const isRequestModalOpen = useModalStore((s) => s.isRequestModalOpen);
  const requestModalClusterType = useModalStore((s) => s.requestModalClusterType);
  const closeRequestModal = useModalStore((s) => s.closeRequestModal);
  const userId = useUserStore((state) => state.userId);

  const routePrefix = env.EXPLORER_COMBINED ? "/explorer" : "";
  const explorerOnlyMode = useExplorerOnlyMode();
  const isContainerLogsFullscreen = /\/cloud\/container\/[^/]+\/logs/.test(location);
  const isExplorerWarningBannerPath =
    location === "/explorer" ||
    location.startsWith("/explorer/") ||
    location === "/worker" ||
    location.startsWith("/worker/") ||
    location === "/staking" ||
    location.startsWith("/staking/");
  const showExplorerWarningBanner =
    isExplorerWarningBannerPath &&
    env.EXPLORER_WARNING_BANNER_ENABLED &&
    Boolean(env.EXPLORER_WARNING_BANNER_DESCRIPTION) &&
    isExplorerWarningBannerVisible;
  const showWarningBanner =
    !showExplorerWarningBanner &&
    env.WARNING_BANNER_ENABLED &&
    Boolean(env.WARNING_BANNER_DESCRIPTION) &&
    isWarningBannerVisible;

  useEffect(() => {
    const { errorTitle, errorMessage } = queryParams;

    if (errorTitle) {
      displayError({
        title: ucfirst(errorTitle),
        message: errorMessage || "Unknown",
        isPublic: true
      });
    }

    installBetterstack();
  }, [queryParams, displayError]);

  useEffect(() => {
    updateToolToLocation();
  }, [location, updateToolToLocation]);

  useEffect(() => {
    setMobileSidebarSheetOpen(false);
  }, [location, setMobileSidebarSheetOpen]);

  useEffect(() => {
    if (isMdUp) {
      setMobileSidebarSheetOpen(false);
    }
  }, [isMdUp, setMobileSidebarSheetOpen]);

  useEffect(() => {
    if (explorerOnlyMode) {
      document.documentElement.classList.remove("app-shell-layout");
      return;
    }

    document.documentElement.classList.add("app-shell-layout");
    return () => {
      document.documentElement.classList.remove("app-shell-layout");
    };
  }, [explorerOnlyMode]);

  useEffect(() => {
    applyThemeForCurrentMode();
  }, [explorerOnlyMode, applyThemeForCurrentMode]);

  useEffect(() => {
    if (explorerOnlyMode) return;
    if (!env.SECTION_THEME_ENABLED || !tool?.value) return;
    if (location.startsWith("/login")) {
      updateTheme("light");
      return;
    }
    if (tool.theme) {
      updateTheme(tool.theme);
    }
  }, [location, tool?.value, tool?.theme, updateTheme, explorerOnlyMode]);

  useEffect(() => {
    if (explorerOnlyMode) return;
    if (!isAuthReady || !isLoggedIn) return;
    if (env.SECTION_THEME_ENABLED) return; // theme is driven by section
    updateTheme(appTheme);
  }, [isAuthReady, isLoggedIn, appTheme, updateTheme, explorerOnlyMode]);

  useEffect(() => {
    // Credits Purchase Success
    const amount = new URLSearchParams(window.location.search).get("amount");
    const currency = new URLSearchParams(window.location.search).get("currency");
    const credits_purchase = new URLSearchParams(window.location.search).get("credits_purchase");
    if (userId && amount && currency && credits_purchase === "success") {
      const url = new URL(window.location.href);
      url.search = "";
      window.history.replaceState({}, document.title, url.toString());
      trackEvent(AnalyticsEventType.PURCHASE_SUCCESS, {
        amount,
        currency
      });
      window.dataLayer.push({
        event: AnalyticsEventType.PURCHASE_SUCCESS,
        ecommerce: {
          currency: "USD",
          value: parseFloat(amount).toFixed(2)
        },
        attribution: {
          ...getFirstTouchAttributions()
        },
        user: {
          user_id: userId
        }
      });
    }

    // Intelligence Plan Purchase
    const plan = new URLSearchParams(window.location.search).get("plan");
    const previousPlan = new URLSearchParams(window.location.search).get("previous_plan");
    const price = new URLSearchParams(window.location.search).get("price");
    const discountCode = new URLSearchParams(window.location.search).get("discount_code");

    if (userId && plan) {
      const url = new URL(window.location.href);
      url.search = "";
      window.history.replaceState({}, document.title, url.toString());

      const utmParams = getFirstTouchAttributions();
      let priceValue = price;
      if (price) {
        priceValue = parseFloat(price).toFixed(2);
      } else {
        priceValue =
          plan === "Professional"
            ? ENV_VARIABLES.INTELLIGENCE_PROFESSIONAL_PRICE
            : ENV_VARIABLES.INTELLIGENCE_DEVELOPER_PRICE;
      }
      trackEvent(AnalyticsEventType.INTEL_PLAN_PURCHASED, {
        plan,
        price: priceValue,
        previousPlan,
        discountCode
      });

      window.dataLayer.push({
        event: AnalyticsEventType.INTEL_PLAN_PURCHASED,
        plan,
        previousPlan,
        discountCode,
        ecommerce: {
          value: parseFloat(priceValue).toFixed(2),
          currency: "USD"
        },
        attribution: {
          ...utmParams
        },
        user: {
          user_id: userId
        }
      });
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const variant = await getVariant(FEATURE_FLAGS[FeatureFlags.PRICE_COMPARISON]);
      if (cancelled || variant.value === "control") return;
      prefetchCompetitorPriceMap();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (env.MAINTENANCE_ENABLED) {
    return <MaintenanceApp />;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={env.RECAPTCHA_SITE_KEY}
      container={{
        parameters: {
          theme: "light"
        }
      }}
    >
      <>
        <p className="hidden">Version - {env.APP_VERSION}</p>

        {showExplorerWarningBanner && (
          <div className="subtext-small absolute z-[99999999] flex w-full flex-row items-center gap-5 bg-gray-dark-500 px-5 py-3 text-foreground">
            <span className="flex-1 text-center">{env.EXPLORER_WARNING_BANNER_DESCRIPTION}</span>
            <CrossIcon
              className="h-[16px] cursor-pointer text-foreground"
              onClick={() => {
                setIsExplorerWarningBannerVisible(false);
              }}
            />
          </div>
        )}

        {showWarningBanner && (
          <div className="subtext-small absolute z-[99999999] flex w-full flex-row items-center gap-5 bg-gray-dark-500 px-5 py-3 text-foreground">
            <span className="flex-1 text-center">{env.WARNING_BANNER_DESCRIPTION}</span>
            <CrossIcon
              className="h-[16px] cursor-pointer text-foreground"
              onClick={() => {
                setIsWarningBannerVisible(false);
              }}
            />
          </div>
        )}

        <RequiresLoginModal
          isOpen={isRequiresLoginModalOpen}
          mode={requiresLoginMode}
          onClose={() => {
            setRequiresLoginModalOpen(false);
            // Drop a pending post-login redirect if the user dismissed the dialog
            // without authenticating (e.g. Manage keys → Account Settings).
            // Read live token — Dialog may call onClose after OTP before React re-renders.
            if (!getToken()) {
              localCookies.removeItem("LOGIN_REDIRECT_ROUTE");
            }
          }}
        />

        <RequestClusterModal
          isOpen={isRequestModalOpen}
          onClose={(open) => (open ? null : closeRequestModal())}
          clusterType={requestModalClusterType}
        />

        <SupportWidget />
        <CanonicalLink />
        <HelpViewer />
        <ErrorViewer />
        {/* <UserWarningViewer /> */}
        <AuthProcess />
        <Toaster />
        <CookieWidget />

        {!isAuthReady ? (
          <LoginVerificationLoader
            message="Checking session..."
            subMessage="Wait a moment, we're restoring your session"
          />
        ) : (
          <Router hook={hook}>
            <DocumentTitleOverrideProvider>
              <DocumentTitle />
              <ClusterDraftNavigationGuard />
              {explorerOnlyMode ? (
                <div
                  className={`flex min-h-screen w-full flex-col ${theme === "dark" ? "dark" : ""}`}
                >
                  <ExplorerHeader />
                  <main className="flex flex-1 flex-col">
                    <PageWrapper includeMainFooter>
                      <AppRoutes routePrefix={routePrefix} />
                    </PageWrapper>
                  </main>
                </div>
              ) : isContainerLogsFullscreen ? (
                <div
                  className={`flex h-screen w-full flex-col overflow-hidden ${isLoggedIn && theme === "dark" ? "dark" : ""}`}
                >
                  <AppRoutes routePrefix={routePrefix} />
                </div>
              ) : (
                <div
                  className={`flex h-screen w-full overflow-hidden ${isLoggedIn && theme === "dark" ? "dark" : ""}`}
                >
                  {isMdUp ? (
                    <div
                      className={`hidden md:flex md:flex-shrink-0 md:overflow-hidden md:bg-container md:transition-[width] md:duration-300 md:ease-in-out ${
                        sidebarCollapsed ? "md:w-[60px]" : "md:w-[271px]"
                      }`}
                    >
                      <LeftSidebar className="overflow-y-auto" />
                    </div>
                  ) : null}
                  {!isMdUp ? (
                    <Sheet open={mobileSidebarSheetOpen} onOpenChange={setMobileSidebarSheetOpen}>
                      <SheetContent
                        side="left"
                        className="w-full max-w-full gap-0 border-r border-border bg-container p-0 sm:max-w-full [&>button]:hidden"
                      >
                        <SheetTitle className="sr-only">Main navigation</SheetTitle>
                        <LeftSidebar
                          className="h-full w-full min-w-0 overflow-y-auto"
                          onCloseRequest={() => setMobileSidebarSheetOpen(false)}
                        />
                      </SheetContent>
                    </Sheet>
                  ) : null}
                  {/* Content: fills the remaining space, then centers its inner content up to the max-width */}
                  <div className="flex min-w-0 flex-1 flex-col overflow-hidden md:bg-container">
                    <div
                      className="mx-auto flex h-full w-full flex-col md:transition-[max-width] md:duration-300 md:ease-in-out"
                      style={{
                        maxWidth: isMdUp ? (isAppContentFullWidth ? "100%" : "1169px") : "100%"
                      }}
                    >
                      <MainHeader />
                      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-0 md:p-4 md:pl-0 md:pt-0">
                        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-container md:rounded-xl md:border md:border-border md:bg-background md:shadow-[-8px_8px_8px_0px_rgba(0,0,0,0.02)]">
                          <PageWrapper includeMainFooter>
                            <AppRoutes routePrefix={routePrefix} />
                          </PageWrapper>
                        </div>
                      </main>
                    </div>
                    {/* end centering wrapper */}
                  </div>
                </div>
              )}
              {/* end explorer-only else branch */}
            </DocumentTitleOverrideProvider>
          </Router>
        )}
      </>
    </GoogleReCaptchaProvider>
  );
};

const LazySignUpPage = () => <LazyComponent path="SignUp/SignUpPage" />;
const LazyCloudHomePage = () => <LazyComponent path="Cloud/HomePage" />;
const LazyManagedServicePage = () => <LazyComponent path="Cloud/ManagedServicePage" />;
const LazyBareMetalOldPage = () => <LazyComponent path="Cloud/BareMetalOldPage" />;
const LazyBareMetalDetailPage = (props: RouteComponentProps) => {
  const detailProps = { ...props, clusterType: "bare-metal" };
  return <LazyComponent {...detailProps} path="Cloud/SimpleClusterDetailPage" />;
};
const LazyConfidentialComputePage = LazyManagedServicePage;
const LazyRayPage = LazyManagedServicePage;
const LazyRayDetailPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Cloud/ClusterDetailPage" />
);
const LazyContainersPage = () => <LazyComponent path="Cloud/ContainersPage" />;
/** Eager: marketing hot paths — avoid extra lazy chunk before /hardware */
const EagerCreateContainerClusterPageV2 = () => <CreateContainerPageV2 />;
const LazyAdjustContainerDeploymentPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Cloud/AdjustContainerDeploymentPage" />
);
const LazyContainerLogsPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Cloud/ContainerLogsPage" />
);
const LazyContainerDetailPage = (props: RouteComponentProps) => {
  const detailProps = { ...props, clusterType: "container" };
  return <LazyComponent {...detailProps} path="Cloud/SimpleClusterDetailPage" />;
};
const LazyKubernetesClustersPage = LazyManagedServicePage;
const LazyCreateKubernetesClusterPageV2 = () => (
  <LazyComponent path="Cloud/CreateKubernetesPageV2" />
);
const LazyKubernetesDetailPage = (props: RouteComponentProps) => {
  const detailProps = { ...props, clusterType: "kubernetes" };
  return <LazyComponent {...detailProps} path="Cloud/SimpleClusterDetailPage" />;
};
const LazyVirtualMachinesPage = () => <LazyComponent path="Cloud/VirtualMachinesPage" />;
const EagerCreateVirtualMachineClusterPageV2 = () => <CreateVirtualMachinePageV2 />;
const LazyVirtualMachineDetailPage = (props: RouteComponentProps) => {
  const detailProps = { ...props, clusterType: "vmaas" };
  return <LazyComponent {...detailProps} path="Cloud/SimpleClusterDetailPage" />;
};

const LazyWorkerHomePage = () => <LazyComponent path="Worker/HomePage" />;
const LazyCreateDevicePage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Worker/CreateDevicePage" />
);
const LazyBillingAndRewardsPage = () => <LazyComponent path="Worker/SupplierDashboardPage" />;
const LazyExplorerHomePage = () => <LazyComponent path="Explorer/HomePage" />;
const LazyExplorerClustersPage = () => <LazyComponent path="Explorer/ClustersPage" />;
const LazyExplorerDevicesPage = () => <LazyComponent path="Explorer/DevicesPage" />;
const LazyInferencePage = () => <LazyComponent path="Explorer/InferencePage" />;
const LazyExplorerStakingDashboard = () => <LazyComponent path="Explorer/StakingDashboardPage" />;
const LazyInferenceDetailPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Explorer/InferenceDetailPage" />
);
const LazyDeviceDetailPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Worker/DeviceDetailPage" />
);
const LazyAccountOverviewPage = () => <LazyComponent path="Account/AccountOverviewPage" />;
const LazyAccountIoCloudPage = () => <LazyComponent path="Account/AccountIoCloudPage" />;
const LazyAccountSettingsPage = () => <LazyComponent path="Account/AccountSettingsPage" />;
const LazyUserAcknowledgmentPage = () => <LazyComponent path="Account/UserAcknowledgmentPage" />;
const LazyUserTransactionsAndBalancePage = () => (
  <LazyComponent path="Account/UserTransactionsAndBalancePage" />
);
const LazySignInSecurityPage = () => <LazyComponent path="Account/SignInSecurityPage" />;
const LazySupportTicketsPage = () => <LazyComponent path="Account/SupportTicketsPage" />;
const LazyNotificationSettingsPage = () => (
  <LazyComponent path="Account/NotificationSettingsPage" />
);
const LazyIoElementsPage = () => <LazyComponent path="Common/IoElementsPage" />;
const LazyIntelligenceUsagePage = () => <LazyComponent path="Account/IntelligenceUsagePage" />;

// Intelligence

// Unified Chat Interface
const LazyUnifiedChatInterfacePage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Intelligence/UnifiedChatInterfacePage" />
);

// AIModel
const LazyAIModelPage = () => <LazyComponent path="Intelligence/AIModelPage" />;

const LazyMAIModelDetailPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Intelligence/AIModelDetailPage" />
);
const LazyMAIModelChatPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Intelligence/AIModelChatPage" />
);
const LazyAPIKeysPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Intelligence/APIKeysPage" />
);

const LazyAIAgentsPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Intelligence/AIAgentsPage" />
);
const LazyTrainingPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Intelligence/TrainingPage" />
);
const LazyCreateTrainingPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Intelligence/CreateTrainingPage" />
);
const LazyTrainingJobDetailsPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Intelligence/TrainingJobDetailsPage" />
);

const LazyEpochRewardsPage = () => <LazyComponent path="Explorer/EpochRewardsPage" />;
const LazyStakingPage = () => <LazyComponent path="Worker/StakingPage" />;
const LazyMyCoStakingDevicesPage = () => <LazyComponent path="Worker/MyCoStakingDevicesPage" />;
const LazySafetyModulePage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Staking/SafetyModulePage" />
);

const LazyFundsPage = () => {
  return <LazyComponent path="Account/FundsPage" />;
};

const LazyFundsDetailPage = () => {
  return <LazyComponent path="Account/FundsDetailPage" />;
};

const RedirectToHomePage = () => {
  const isLoggedIn = useAuthState((state) => state.status === "authenticated");
  const subdomainHome = getSubdomainToolHomePath();
  const defaultRoute = getDefaultRouteAfterLogin();

  if (!isLoggedIn) {
    const fallback = subdomainHome ?? defaultRoute;
    return <Redirect to={urlWithCurrentQuery(fallback)} />;
  }

  const subdomainDefault = subdomainHome ?? defaultRoute;
  return <Redirect to={urlWithCurrentQuery(redirectUsingQuery(subdomainDefault))} />;
};

const LoginPageContent = ({ whenAuthenticated }: { whenAuthenticated?: ReactNode }) => {
  const status = useAuthState((state) => state.status);
  const isReady = useAuthState((state) => state.isReady);
  const isLoggedIn = status === "authenticated";
  const queryParams = useQueryParams<{ code?: string; redirect?: string; state?: string }>();
  const hasCodeParam = !!queryParams.code;
  const loginRedirectRoute = localCookies.getItem("LOGIN_REDIRECT_ROUTE");
  const hasLoginRedirectRoute = !!loginRedirectRoute;
  const hasRedirectQuery = !!queryParams.redirect;
  const isBootstrapping = status === "bootstrapping";
  const isVerifying = hasCodeParam || (hasLoginRedirectRoute && (isBootstrapping || !isReady));

  if (isLoggedIn) {
    if (whenAuthenticated) {
      return whenAuthenticated;
    }

    const defaultRouteAfterLogin = getDefaultRouteAfterLogin();
    if ((hasRedirectQuery || hasLoginRedirectRoute) && !loginRedirectRoute?.includes("/login")) {
      return <Redirect to={urlWithCurrentQuery(redirectUsingQuery(defaultRouteAfterLogin))} />;
    }
    return <Redirect to={defaultRouteAfterLogin} />;
  }

  if (isVerifying) {
    const message = hasCodeParam ? "Verifying..." : "Checking session...";
    const subMessage = hasLoginRedirectRoute
      ? "Wait a moment, we're restoring your session"
      : "Wait a moment, we're taking you back to your destination";
    return <LoginVerificationLoader message={message} subMessage={subMessage} />;
  }

  return <LazySignUpPage />;
};

const LoginPageWrapper = () => <LoginPageContent />;

const CloudDashboardRoute = () => <LoginPageContent whenAuthenticated={<LazyDashboardPage />} />;

const LazyMaintenancePage = () => {
  return <LazyComponent path="Common/MaintenancePage" />;
};

const MaintenanceApp = () => {
  useDocumentTitle("Maintenance");
  return <LazyMaintenancePage />;
};

const LazyDashboardPage = () => {
  return <LazyComponent path="Dashboard/DashboardPage" />;
};

const LazyExplorerInventoryPage = () => {
  return <LazyComponent path="Explorer/Inventory" />;
};

const IntelligenceMaintenancePage = () => (
  <MaintenancePage
    title="🚀 Whoa! We're Blowing Up! 🚀"
    description="Looks like everyone wants in at the same time—our servers are working overtime! <br> We're scaling up as fast as we can. Hang tight and try again in a few moments."
  />
);

const LazyAIAgenticWorkflowEditorPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Intelligence/AgenticWorkflowEditorPage" />
);
const LazyPlansPage = () => <LazyComponent path="Intelligence/PlansPage" />;
const LazyEpochsPage = () => <LazyComponent path="Explorer/EpochsPage" />;
const LazyEpochDetailPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Explorer/EpochDetailPage" />
);

const LazyClusterDetailPage = (props: RouteComponentProps) => (
  <LazyComponent {...props} path="Cloud/ClusterDetailPage" />
);

/** Full application route table — shared by both the normal shell and EXPLORER_ONLY_MODE. */
const AppRoutes = ({ routePrefix }: { routePrefix: string }) => {
  const explorerOnlyMode = useExplorerOnlyMode();

  return (
    <Switch>
      <Route path="/login" component={LoginPageWrapper} />
      <Route path="/login/signup" component={LoginPageWrapper} />
      <AuthenticatedRoute path="/login/:section" component={LazySignUpPage} />
      {env.EXPLORER_COMBINED && (
        <>
          <Route path="/explorer/login/signup" component={() => <Redirect to="/login/signup" />} />
          <Route
            path="/explorer/login/:section"
            component={({ params }: RouteComponentProps<{ section: string }>) => (
              <Redirect to={`/login/${params.section}`} />
            )}
          />
          <Route path="/explorer/login" component={() => <Redirect to="/login" />} />
        </>
      )}
      <Route path="/" component={RedirectToHomePage} />

      {/* Dashboard — unavailable in EXPLORER_ONLY_MODE */}
      {explorerOnlyMode ? (
        <>
          <Route path="/dashboard" component={() => <Redirect to="/explorer/home" />} />
          <Route path="/explorer/dashboard" component={() => <Redirect to="/explorer/home" />} />
          <Route path="/cloud/dashboard" component={() => <Redirect to="/explorer/home" />} />
        </>
      ) : (
        <>
          <ConditionalAuthRoute
            path="/dashboard"
            component={() => <Redirect to="/cloud/dashboard" />}
          />
          <Route path="/explorer/dashboard" component={() => <Redirect to="/cloud/dashboard" />} />
          <Route path="/cloud/dashboard" component={CloudDashboardRoute} />
        </>
      )}

      {/* Cloud */}
      <Route path="/cloud" component={() => <Redirect to="/cloud/home" />} />
      <ConditionalAuthRoute path="/cloud/home" component={LazyCloudHomePage} />

      <Route path="/cloud/clusters" component={() => <Redirect to="/cloud/dashboard" />} />

      {/* Legacy cluster notification links resolve to the correct resource detail page */}
      <ConditionalAuthRoute path="/cloud/clusters/:id" component={RedirectToClusterDetailsPage} />

      {env.FEATUREFLAG_BARE_METAL_DEPLOY && (
        <ConditionalAuthRoute path="/cloud/bare-metal" component={LazyManagedServicePage} />
      )}
      <ConditionalAuthRoute path="/cloud/bare-metal-old" component={LazyBareMetalOldPage} />
      {env.FEATUREFLAG_KUBERNETES_DEPLOY && (
        <ConditionalAuthRoute path="/cloud/kubernetes" component={LazyKubernetesClustersPage} />
      )}
      {env.FEATUREFLAG_KUBERNETES_DEPLOY && (
        <ConditionalAuthRoute
          path="/cloud/kubernetes/create-cluster"
          component={LazyCreateKubernetesClusterPageV2}
        />
      )}
      {env.FEATUREFLAG_KUBERNETES_DEPLOY && (
        <AuthenticatedRoute path="/cloud/kubernetes/:id" component={LazyKubernetesDetailPage} />
      )}
      {env.FEATUREFLAG_CONTAINER_DEPLOY && (
        <ConditionalAuthRoute path="/cloud/container" component={LazyContainersPage} />
      )}
      {env.FEATUREFLAG_CONTAINER_DEPLOY && (
        <ConditionalAuthRoute
          path="/cloud/container/create-cluster"
          component={EagerCreateContainerClusterPageV2}
        />
      )}
      {env.FEATUREFLAG_CONTAINER_DEPLOY && (
        <>
          <AuthenticatedRoute
            path="/cloud/container/:id/adjust"
            component={LazyAdjustContainerDeploymentPage}
          />
          <AuthenticatedRoute path="/cloud/container/:id/logs" component={LazyContainerLogsPage} />
          <AuthenticatedRoute path="/cloud/container/:id" component={LazyContainerDetailPage} />
        </>
      )}

      {/* Cloud - Bare Metal */}
      <ConditionalAuthRoute
        path="/cloud/confidential-compute"
        component={LazyConfidentialComputePage}
      />
      <AuthenticatedRoute path="/cloud/ray/:id" component={LazyRayDetailPage} />
      <ConditionalAuthRoute path="/cloud/ray" component={LazyRayPage} />
      {env.FEATUREFLAG_BARE_METAL_DEPLOY && (
        <AuthenticatedRoute path="/cloud/bare-metal/:id" component={LazyBareMetalDetailPage} />
      )}

      {/* Cloud - VMaaS */}
      {env.FEATUREFLAG_VMAAS_DEPLOY && (
        <>
          <ConditionalAuthRoute
            path="/cloud/virtual-machines"
            component={LazyVirtualMachinesPage}
          />
          <ConditionalAuthRoute
            path="/cloud/virtual-machines/create-cluster"
            component={EagerCreateVirtualMachineClusterPageV2}
          />
          <AuthenticatedRoute
            path="/cloud/virtual-machines/:id"
            component={LazyVirtualMachineDetailPage}
          />
        </>
      )}

      {/* AI */}
      <Route path="/ai" component={IntelligenceDefaultPage} />
      <Route path="/ai/app" component={() => <Redirect to="/ai/app/new" />} />
      <AuthenticatedRoute path="/ai/app/:id" component={LazyUnifiedChatInterfacePage} />
      <ConditionalAuthRoute path="/ai/plans" component={LazyPlansPage} />
      <ConditionalAuthRoute
        path="/ai/models"
        component={
          !env.INTELLIGENCE_MAINTENANCE_ENABLED ? LazyAIModelPage : IntelligenceMaintenancePage
        }
      />
      <ConditionalAuthRoute
        path="/ai/models/:id/"
        component={
          !env.INTELLIGENCE_MAINTENANCE_ENABLED
            ? LazyMAIModelDetailPage
            : IntelligenceMaintenancePage
        }
      />
      <ConditionalAuthRoute
        path="/ai/models/:id/chat/:chatId"
        component={
          !env.INTELLIGENCE_MAINTENANCE_ENABLED ? LazyMAIModelChatPage : IntelligenceMaintenancePage
        }
      />
      <ConditionalAuthRoute
        path="/ai/api-keys"
        component={
          !env.INTELLIGENCE_MAINTENANCE_ENABLED ? LazyAPIKeysPage : IntelligenceMaintenancePage
        }
      />
      <ConditionalAuthRoute
        path="/ai/agents"
        component={
          !env.INTELLIGENCE_MAINTENANCE_ENABLED ? LazyAIAgentsPage : IntelligenceMaintenancePage
        }
      />
      <AuthenticatedRoute
        path="/ai/agentic-workflow-editor"
        component={
          !env.INTELLIGENCE_MAINTENANCE_ENABLED
            ? LazyAIAgenticWorkflowEditorPage
            : IntelligenceMaintenancePage
        }
      />
      <AuthenticatedRoute
        path="/ai/agentic-workflow-editor/:workflowId"
        component={
          !env.INTELLIGENCE_MAINTENANCE_ENABLED
            ? LazyAIAgenticWorkflowEditorPage
            : IntelligenceMaintenancePage
        }
      />
      <AuthenticatedRoute path="/ai/training" component={LazyTrainingPage} />
      <AuthenticatedRoute path="/ai/training/create" component={LazyCreateTrainingPage} />
      <AuthenticatedRoute
        path="/ai/training/job"
        component={() => <Redirect to="/ai/training" />}
      />
      <AuthenticatedRoute path="/ai/training/job/:jobId" component={LazyTrainingJobDetailsPage} />

      {/* Public explorer workers — /worker/devices/explorer/* */}
      <Route path="/worker/devices/explorer/inventory" component={LazyExplorerInventoryPage} />
      <Route path="/worker/devices/explorer" component={LazyExplorerDevicesPage} />
      <Route path="/worker/devices/explorer/:id" component={LazyDeviceDetailPage} />

      {/* Logged-in worker dashboard — /worker/dashboard/* */}
      <Route path="/worker" component={() => <Redirect to="/worker/dashboard/devices" />} />
      <Route
        path="/worker/dashboard"
        component={() => <Redirect to="/worker/dashboard/devices" />}
      />
      <ConditionalAuthRoute
        path="/worker/dashboard/staking/devices/:section"
        component={LazyStakingPage}
      />
      <ConditionalAuthRoute path="/worker/dashboard/staking/devices" component={LazyStakingPage} />
      <ConditionalAuthRoute
        path="/worker/dashboard/earnings-and-rewards"
        component={LazyBillingAndRewardsPage}
      />
      <ConditionalAuthRoute
        path="/worker/dashboard/devices/create/:id"
        component={LazyCreateDevicePage}
      />
      <ConditionalAuthRoute path="/worker/dashboard/devices/:id" component={LazyDeviceDetailPage} />
      <ConditionalAuthRoute path="/worker/dashboard/devices" component={LazyWorkerHomePage} />

      {/* Legacy worker dashboard paths */}
      <Route
        path="/worker/staking/devices/:section"
        component={({ params }: RouteComponentProps<{ section: string }>) => (
          <Redirect to={`/worker/dashboard/staking/devices/${params.section}`} />
        )}
      />
      <Route
        path="/worker/staking/devices"
        component={() => <Redirect to="/worker/dashboard/staking/devices" />}
      />
      <Route
        path="/worker/earnings-and-rewards"
        component={() => <Redirect to="/worker/dashboard/earnings-and-rewards" />}
      />
      <Route
        path="/worker/devices/create/:id"
        component={({ params }: RouteComponentProps<{ id: string }>) => (
          <Redirect to={`/worker/dashboard/devices/create/${params.id}`} />
        )}
      />
      <Route
        path="/worker/devices/:id"
        component={({ params }: RouteComponentProps<{ id: string }>) =>
          params.id === "explorer" ? (
            <Redirect to="/worker/devices/explorer" />
          ) : (
            <Redirect to={`/worker/dashboard/devices/${params.id}`} />
          )
        }
      />
      <Route path="/worker/devices" component={() => <Redirect to="/worker/dashboard/devices" />} />
      {env.FEATUREFLAG_CO_STAKING_ENABLED && (
        <>
          <ConditionalAuthRoute
            path={`${routePrefix}/staking`}
            component={() => <Redirect to={`${routePrefix}/staking/my-co-staking`} />}
          />
          <ConditionalAuthRoute
            path={`${routePrefix}/staking/my-co-staking`}
            component={LazyMyCoStakingDevicesPage}
          />
        </>
      )}
      {env.FEATUREFLAG_SAFETY_MODULE_ENABLED && (
        <AuthenticatedRoute
          path={`${routePrefix}/staking/safety-module`}
          component={LazySafetyModulePage}
        />
      )}

      {/* Explorer */}
      <Route path="/explorer" component={() => <Redirect to="/explorer/home" />} />
      <Route path="/explorer/home" component={LazyExplorerHomePage} />
      <Route path="/explorer/clusters" component={LazyExplorerClustersPage} />
      <Route path="/explorer/clusters/:id" component={LazyClusterDetailPage} />
      <Route
        path="/explorer/devices/inventory"
        component={() => <Redirect to="/worker/devices/explorer/inventory" />}
      />
      <Route
        path="/explorer/devices/:id"
        component={({ params }: RouteComponentProps<{ id: string }>) => (
          <Redirect to={`/worker/devices/explorer/${params.id}`} />
        )}
      />
      <Route
        path="/explorer/devices"
        component={() => <Redirect to="/worker/devices/explorer" />}
      />
      <Route path="/explorer/map" component={() => <Redirect to="/explorer/home" />} />
      <Route path="/explorer/inferences" component={LazyInferencePage} />
      <Route path="/explorer/inferences/:id" component={LazyInferenceDetailPage} />
      <Route path="/explorer/epoch-rewards" component={LazyEpochRewardsPage} />
      <Route
        path="/explorer/block-rewards"
        component={() => <Redirect to="/explorer/epoch-rewards" />}
      />
      {env.FEATUREFLAG_STAKING_DASHBOARD_ENABLED && (
        <Route path="/explorer/staking-dashboard" component={LazyExplorerStakingDashboard} />
      )}
      <Route path="/explorer/epochs/:epoch_id" component={LazyEpochDetailPage} />
      <Route path="/explorer/epochs" component={LazyEpochsPage} />
      <Route path="/explorer/transactions" component={() => <Redirect to="/explorer/epochs" />} />
      <AuthenticatedRoute path="/:tool/devices/:id?*" component={LazyDeviceDetailPage} />

      {/* Account */}
      <AuthenticatedRoute
        path={`${routePrefix}/id/account/overview`}
        component={LazyAccountOverviewPage}
      />
      <AuthenticatedRoute
        path={`${routePrefix}/id/account/io-cloud`}
        component={LazyAccountIoCloudPage}
      />
      <AuthenticatedRoute
        path={explorerPrefixedPath("/id/account/settings")}
        component={LazyAccountSettingsPage}
      />
      <AuthenticatedRoute
        path={`${routePrefix}/id/account/intelligence`}
        component={LazyIntelligenceUsagePage}
      />
      <AuthenticatedRoute
        path={explorerPrefixedPath("/id/account/acknowledgment")}
        component={LazyUserAcknowledgmentPage}
      />
      <AuthenticatedRoute
        path={`${routePrefix}/id/account/transactions-and-balance`}
        component={LazyUserTransactionsAndBalancePage}
      />
      <AuthenticatedRoute
        path={`${routePrefix}/id/account/security`}
        component={LazySignInSecurityPage}
      />
      <AuthenticatedRoute
        path={`${routePrefix}/id/account/support`}
        component={LazySupportTicketsPage}
      />
      <AuthenticatedRoute
        path={`${routePrefix}/id/account/notification`}
        component={LazyNotificationSettingsPage}
      />
      <AuthenticatedRoute
        path={`${routePrefix}/id/account/io-elements`}
        component={LazyIoElementsPage}
      />
      <AuthenticatedRoute path={explorerPrefixedPath("/id/funds")} component={LazyFundsPage} />
      <AuthenticatedRoute
        path={`${explorerPrefixedPath("/id/funds")}/:id`}
        component={LazyFundsDetailPage}
      />
      {env.EXPLORER_COMBINED && (
        <>
          <Route path="/explorer/id/funds" component={() => <Redirect to="/cloud/id/funds" />} />
          <Route
            path="/explorer/id/funds/:id"
            component={({ params }: RouteComponentProps<{ id: string }>) => (
              <Redirect to={`/cloud/id/funds/${params.id}`} />
            )}
          />
          <Route
            path="/explorer/id/account/settings"
            component={() => <Redirect to="/cloud/id/account/settings" />}
          />
          <Route
            path="/explorer/id/account/acknowledgment"
            component={() => <Redirect to="/cloud/id/account/acknowledgment" />}
          />
        </>
      )}
    </Switch>
  );
};
